import frontmatter from 'front-matter';
import type * as hast from 'hast';
import type * as mdast from 'mdast';
import rehypeMinifyWhitespace from 'rehype-minify-whitespace';
import rehypeRaw from 'rehype-raw';
import remarkHeadingId from 'remark-heading-id';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';

import type { Document } from '@gitbook/slate';
import { Trace } from '@gitbook/util-debug';

import type { FrontmatterAttributes } from '../../pages';
import { footnoteIdToHtmlId, remarkGfm } from '../../utils'; // mdast-util-gfm-table
import { createParsingContext } from '../context';
import { hastToGast } from '../html';
import { defaultParsingOptions, type ParsingOptions } from '../options';
import { remarkEmojis } from './emojis';
import { parseLiquidProperties, processLiquidTags } from './liquid';

export type ParseMarkdownResult = { document: Document; attributes: FrontmatterAttributes };

export type ParseMarkdownOptions = ParsingOptions & {
    /**
     * Defines the way soft-breaks are handled.
     * By default, soft-breaks are minified and replaced with a whitespace. This matches the behavior of GitHub or other markdown renderers.
     * When parsing Markdown as a loose input text (ex: when pasting from the clipboard), we want to preserve the soft-breaks.
     * @default false.
     */
    preserveSoftBreaks?: boolean;
};

/**
 * Parse a markdown string into a GitBook document.
 */
export function parseMarkdown(
    input: string,
    options: ParseMarkdownOptions = defaultParsingOptions
): Promise<ParseMarkdownResult> {
    const { preserveSoftBreaks = false } = options;

    const trace = new Trace('format.parseMarkdown', { silent: true, sampleRate: 0 });
    const context = createParsingContext(options);

    return trace.wrap(async () => {
        // Extract the frontmatter
        const parsed = frontmatter<FrontmatterAttributes>(input);

        // Process the liquid tags into HTML
        const markdownSource = processLiquidTags(parsed.body);

        const file = await unified()
            // Parse the markdown with GitHub syntax
            .use(remarkParse)
            .use(remarkGfm)
            .use(remarkMath, {
                singleDollarTextMath: false,
            })
            .use(remarkEmojis)
            .use(remarkHeadingId)

            // Transform the markdown into HTML
            .use(remarkRehype, {
                allowDangerousHtml: true,
                handlers: {
                    // mdast-util-to-hast encode the url. we want the original url parsed from markdown.
                    // https://github.com/syntax-tree/mdast-util-to-hast/blob/9.1.2/lib/handlers/link.js#L9
                    link: (state, node: mdast.Link) => {
                        const props: { href: string; title?: string } = { href: node.url };

                        if (node.title !== null && node.title !== undefined) {
                            props.title = node.title;
                        }

                        const element: hast.Element = {
                            type: 'element',
                            tagName: 'a',
                            properties: props,
                            children: state.all(node),
                        };

                        return element;
                    },

                    // rehype-raw removes the `data` generated by `remark-rehype` to store the `meta` of the node.
                    // we need to keep it to be able to retrieve the `meta` later in HTML parsing
                    code: (_state, node: mdast.Code) => {
                        const value = node.value ? `${node.value}\n` : '';
                        const props: {
                            className?: string[];
                            dataMeta?: string;
                        } = {};

                        if (node.lang) {
                            props.className = [`language-${node.lang}`];
                        }

                        if (node.meta) {
                            // If meta is a liquid string that we generated
                            // we replace it by actual data properties on the HTML element
                            const metaProps = parseLiquidProperties(node.meta);

                            // Because HTML data attributes are case insensitive, we store the keys and values
                            // in a separate object when they might be case sensitive or include dashes.
                            // We stringify them in the HTML to prevent losing the information.
                            if (Object.keys(metaProps).length > 0) {
                                props['data-gitbook-props'] = JSON.stringify(metaProps);
                            } else {
                                props.dataMeta = node.meta;
                            }
                        }

                        const code: hast.Element = {
                            type: 'element',
                            tagName: 'code',
                            properties: props,
                            children: [
                                {
                                    type: 'text',
                                    value,
                                },
                            ],
                        };

                        return {
                            type: 'element',
                            tagName: 'pre',
                            properties: {},
                            children: [code],
                        };
                    },

                    // Custom handlers for footnotes as the default one is appending a link back to the reference.
                    // This implementation is more direct, and only creates a single link.
                    footnoteReference: (state, node: mdast.FootnoteReference) => {
                        return {
                            type: 'element',
                            tagName: 'a',
                            properties: {
                                href: `#${footnoteIdToHtmlId(node.identifier)}`,
                                'data-footnote-ref': true,
                            },
                            children: state.all(node),
                        };
                    },
                    footnoteDefinition: (state, node: mdast.FootnoteDefinition) => {
                        return {
                            type: 'element',
                            tagName: 'section',
                            properties: {
                                'data-footnotes': true,
                            },
                            children: [
                                {
                                    type: 'element',
                                    tagName: 'ol',
                                    properties: {},
                                    children: [
                                        {
                                            type: 'element',
                                            tagName: 'li',
                                            properties: {
                                                id: footnoteIdToHtmlId(node.identifier),
                                            },
                                            children: state.all(node),
                                        },
                                    ],
                                },
                            ],
                        };
                    },
                },
            })
            .use(rehypeRaw)

            .use(rehypeMinifyWhitespace, { newlines: preserveSoftBreaks })

            // Process the raw HTML in the markdown
            // Transform the HTML tree into a document
            .use(hastToGast, context)
            .process(markdownSource);

        // @ts-expect-error
        const { document, attributes } = await file.result;

        return {
            document,
            attributes: { ...parsed.attributes, ...attributes },
        };
    });
}
